/**
 * @file Singleton Uppy instance lifecycle and module-level multipart stores.
 * Stores survive React StrictMode remounts by design.
 */

import type Uppy from '@uppy/core';

import { createUploadManagerUppy } from './create-upload-manager-uppy';
import { registerUploadServiceWorker } from './register-upload-service-worker';
import { initUploadKindAdapters } from './upload-kind-registry';

import type { PartUrlStore } from './adapters/create-s3-multipart-plugin';
import type { PersistedMultipartContext } from './types';
import type { UploadKindAdapter } from './upload-kind-adapter';

type Meta = Record<string, unknown>;
type Body = Record<string, unknown>;

/** In-memory S3 part presigned URLs keyed by part number. Survives StrictMode remounts. */
export const uploadManagerPartUrlStore: PartUrlStore = {};

/** In-memory multipart registration contexts keyed by Uppy file id. */
export const uploadManagerPersistedContexts = new Map<
  string,
  PersistedMultipartContext
>();

let uppyInstance: Uppy<Meta, Body> | null = null;
let uppyInitPromise: Promise<Uppy<Meta, Body>> | null = null;
let uploadManagerConfigured = false;

/**
 * Registers the GoldenRetriever service worker if supported.
 * @returns Service worker registration or null when unavailable.
 */
export const ensureUploadServiceWorkerReady =
  (): Promise<ServiceWorkerRegistration | null> => {
    return registerUploadServiceWorker();
  };

/**
 * Initializes the upload kind registry. Idempotent — runs once per page load.
 * @param adapters - Feature adapters passed from {@link UploadsProvider}.
 */
export const initUploadManager = (adapters: UploadKindAdapter[]): void => {
  if (uploadManagerConfigured) {
    return;
  }

  initUploadKindAdapters(adapters);
  uploadManagerConfigured = true;
};

/**
 * Creates the singleton Uppy instance after the service worker is registered.
 * GoldenRetriever must not initialize before SW registration completes.
 */
export const ensureUploadManagerUppy = (): Promise<Uppy<Meta, Body>> => {
  if (uppyInstance) {
    return Promise.resolve(uppyInstance);
  }

  if (!uploadManagerConfigured) {
    return Promise.reject(
      new Error('Upload manager not configured. Pass kinds to UploadsProvider.')
    );
  }

  if (!uppyInitPromise) {
    uppyInitPromise = ensureUploadServiceWorkerReady().then(() => {
      uppyInstance = createUploadManagerUppy({
        partUrlStore: uploadManagerPartUrlStore,
        persistedContexts: uploadManagerPersistedContexts,
      });
      return uppyInstance;
    });
  }

  return uppyInitPromise;
};

/**
 * Returns the singleton Uppy instance after {@link ensureUploadManagerUppy} resolves.
 * @throws When called before Uppy has been initialized.
 */
export const getUploadManagerUppy = (): Uppy<Meta, Body> => {
  if (!uppyInstance) {
    throw new Error(
      'Upload manager Uppy not initialized. Await ensureUploadManagerUppy() first.'
    );
  }

  return uppyInstance;
};
